import { Analytics, ContentCopy, Download, MoreHoriz } from '@mui/icons-material';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { styled } from '@mui/system';
import * as fileSaver from 'file-saver';
import React, {
  Children,
  cloneElement,
  ComponentPropsWithoutRef,
  isValidElement,
  ReactNode,
  useRef,
  useState,
} from 'react';
import type { ReactMarkdownProps } from 'react-markdown/lib/complex-types';
import { useParams } from 'react-router-dom';

import useCopyToClipboard from '../hooks/useCopyToClipboard';
import { parseTableToCSV } from '../util/stringUtils';
import { useChat } from './Providers/ChatProvider';

type MarkdownTableProps = ComponentPropsWithoutRef<'table'> &
  ReactMarkdownProps & {
    loading?: boolean;
  };

const StyledTable = styled('table')({
  borderSpacing: '0',
  borderCollapse: 'collapse',
  display: 'block',
  marginTop: '0',
  marginBottom: '0',
  width: 'max-content',
  maxWidth: '100%',
  overflow: 'auto',

  tr: {
    backgroundColor: '#ffffff',
    borderTop: '1px solid ' + 'hsla(210, 18%, 87%, 1)',

    '&:nth-of-type(2n)': {
      backgroundColor: '#f6f8fa',
    },
  },

  'td, th': {
    padding: '6px 13px',
    border: '1px solid ' + '#d0d7de',
  },

  th: {
    fontWeight: '600',
  },

  img: {
    backgroundColor: 'transparent',
  },

  'a, a:visited, a:hover, a:focus': {
    color: 'inherit',
  },
});

// Function to parse HTML tags in text
const parseHtmlContent = (content: ReactNode): ReactNode => {
  // If the content is a string, process HTML tags
  if (typeof content === 'string') {
    // Split the content by <br> tags and join with actual line breaks
    const parts = content.split('<br>');
    if (parts.length === 1) {
      return content; // No <br> tags found, return as is
    }

    // Create an array of elements with React fragments for breaks
    return parts.map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < parts.length - 1 && <br />}
      </React.Fragment>
    ));
  }

  // If content is an array, process each item
  if (Array.isArray(content)) {
    return content.map((child, index) => (
      <React.Fragment key={index}>{parseHtmlContent(child)}</React.Fragment>
    ));
  }

  // If content is a React element, process its children
  if (isValidElement(content)) {
    const childrenArray = Children.toArray(content.props.children);
    if (childrenArray.length > 0) {
      return cloneElement(
        content,
        content.props,
        ...Children.map(childrenArray, (child) => parseHtmlContent(child))
      );
    }
  }

  // Return as is for other types
  return content;
};

const MarkdownTable = ({ className, loading, children }: MarkdownTableProps) => {
  const { copySuccess, copyToClipboard } = useCopyToClipboard();
  const { conversationId } = useParams();
  const { handleSubmit } = useChat();
  const [anchorContextMenu, setAnchorContextMenu] = useState<null | HTMLElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const handleCopyToClipboard = () => {
    if (!tableRef.current) return;
    const tableText = tableRef.current.innerText;
    copyToClipboard(tableText);
  };

  const handleCopyToCSV = () => {
    if (!tableRef.current) return;
    const tableText = tableRef.current.innerText;

    const parsedCSVContent = parseTableToCSV(tableText);
    const fileName = `${parsedCSVContent.split(',')[0]}_csv_export.csv`;
    fileSaver.saveAs(new Blob([parsedCSVContent], { type: 'text/csv' }), fileName);
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorContextMenu(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorContextMenu(null);
  };

  return (
    <div>
      <Tooltip title="Open table options">
        <div>
          <IconButton onClick={handleOpenMenu} disabled={loading}>
            <MoreHoriz />
          </IconButton>
        </div>
      </Tooltip>
      <Menu
        open={Boolean(anchorContextMenu)}
        anchorEl={anchorContextMenu}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleCopyToClipboard}>
          <ListItemIcon>
            <ContentCopy />
          </ListItemIcon>
          <ListItemText>{copySuccess ? 'Copied!' : 'Copy table to clipboard'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyToCSV}>
          <ListItemIcon>
            <Download />
          </ListItemIcon>
          <ListItemText>Download CSV</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleSubmit({
              conversationId,
              input:
                'Generate html code to create an entire .html file that can visualize this data using chart.js. Do not truncate the data or the code. ',
            })
          }
        >
          <ListItemIcon>
            <Analytics />
          </ListItemIcon>
          <ListItemText>Generate Visualization</ListItemText>
        </MenuItem>
      </Menu>
      <StyledTable className={className} ref={tableRef}>
        {parseHtmlContent(children)}
      </StyledTable>
      <p style={{ opacity: '0.5' }}>
        <i>
          Note: Don&apos;t use Nigel for any math operations on this table, it will be incorrect.
        </i>
      </p>
    </div>
  );
};

export default MarkdownTable;
